import React, {useState, useEffect, useRef} from 'react'
import { AxiosError } from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";
import { isValidEmail } from 'src/shared/utils/src';
import { loginParent, sendVerificationCode } from 'src/shared/services/src';
import { APP_MESSAGES, LOGIN_OTP_TYPE } from 'src/shared/config/src';
import { ErrorType } from 'src/shared/types/src';
import Footer from 'src/app/components/commonComponents/Footer';
import Images from '../../static'
import CustomButton from 'src/app/components/commonComponents/CustomButton';
import CustomTextInput from 'src/app/components/commonComponents/CustomTextInput';
import { CheckmarkCircle, Eye, EyeOff } from 'react-ionicons';
import DownloadAppComponent from 'src/app/components/commonComponents/DownloadAppComponent';
import { AppRoutes } from 'src/app/navigation/routes';
import { URL_CONSTANTS } from 'src/app/config/appConstants';
import { getRedirectionRoute } from 'src/app/utilities/helpers';
// import { loginParent, performAccountValidation, sendVerificationCode } from '@parent-app/services';
// import { ErrorType } from '@parent-app/types';
// import { isValidEmail, validatePassword } from '@parent-app/utils';
// import { LOGIN_OTP_TYPE } from '@parent-app/config';

const FIELD_MAP = {
  EMAIL: 'email',
  PASSWORD: 'password'
};

const LoginScreen = () => {

  const location = useLocation();
  const navigate = useNavigate()
  const inputRef: any = useRef(null);
  const buttonRef: any = useRef(null);
  const { email } = location.state || {};

  const [isLoggingIn, setIsLoggingIn] = useState<boolean>(false);
  const [errors, setErrors] = useState<ErrorType>({});
  const [resending, setResending] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [loginData, setLoginData] = useState<{email: string, password: string}>({
    email: email || '',
    password: ''
  })
  const [redirectionRoute, setRedirectionRoute] = useState<string>();

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
    const redirectTo = getRedirectionRoute();
    if (redirectTo) {
      setRedirectionRoute(redirectTo);
    }
  }, []);

  const handleKeyDown = (event: any) => {
    if (event.key === 'Enter' && buttonRef.current) {
      buttonRef.current.click();
    }
  };

  useEffect(() => {
    if (!email) {
      navigate(AppRoutes.VERIFY_EMAIL);
    }
  }, [email])

  const runValidations = () => {
    let errorObj: ErrorType = {};
    if (!loginData.email) {
      errorObj = {
        ...errorObj,
        [FIELD_MAP.EMAIL]: {
          isErrored: true,
          errorMsg: `Email cannot be blank`,
        },
      };
    } else if (!isValidEmail(loginData.email)) {
      errorObj = {
        ...errorObj,
        [FIELD_MAP.EMAIL]: {
          isErrored: true,
          errorMsg: `Email is invalid`,
        },
      };
    }

    if (!loginData.password) {
      errorObj = {
        ...errorObj,
        [FIELD_MAP.PASSWORD]: {
          isErrored: true,
          errorMsg: `Password cannot be blank`,
        },
      };
    }

    setErrors(errorObj);

    if (Object.keys(errorObj).length) return false;
    else return true;
  }

  const handleInputChange = (event: any) => {
    setLoginData({
      ...loginData,
      [event.target.name]: event.target.value
    })
  }

  const handleLogin = async () => {
    if (isLoggingIn) return;

    const validity = runValidations();
    if (!validity) return;

    try {
      setErrors({});
      setIsLoggingIn(true);
      const response = await loginParent({ email: loginData.email, password: loginData.password });
      if (response && Object.keys(response).length > 0) {
        const { multiFactorRequired, sessionId, username } = response;
        if (multiFactorRequired) {
          navigate(`${AppRoutes.VERIFY_OTP}${redirectionRoute ? `?${URL_CONSTANTS.REDIRECT_TO}=${redirectionRoute}` : ''}`, { state: { email: username, sessionId, isMFACall: true }});
        } else {
          toast.error(APP_MESSAGES.generalErrorMessage, { autoClose: 5000 });
        }
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const apiError = error as AxiosError;
        if (apiError && apiError.response) {
          const backendErrors = error.response?.data;
          console.log("backendErrors : ", backendErrors);
          const { errors } = backendErrors;
          toast.error(errors.join(", "), { autoClose: 5000 });
        }
      }
    } finally {
      setIsLoggingIn(false);
    }
  }

  const handleAccountVerification = async () => {
    if (resending) return;
    try {
      setResending(true);
      const { sessionId } = await sendVerificationCode({ email, type: LOGIN_OTP_TYPE.ACTIVATION });
      if (sessionId) {
        toast.success('Verification code sent successfully', { autoClose: 5000 });
        navigate(`${AppRoutes.VERIFY_OTP}${redirectionRoute ? `?${URL_CONSTANTS.REDIRECT_TO}=${redirectionRoute}` : ''}`, {state: { sessionId, email }});
      } else {
        toast.error("Could not resend code.", { autoClose: 5000 });
      }
    } catch (error) {
      console.log("error from send otp : ", error);
      if (error instanceof AxiosError) {
        const apiError = error as AxiosError;
        console.log("error  : ", error.response);
        if (apiError && apiError.response) {
          const { errors } = error.response?.data;
          toast.error(errors.join(", "), { autoClose: 5000 });
        } else {
          toast.error(APP_MESSAGES.generalErrorMessage, { autoClose: 5000 });
        }
      }
    } finally {
      setResending(false);
    }
  }
  return (
    <div className='main-wrapper bg-white container'>
      <div className='d-flex flex-column flex-grow-1 mg-b-20'>
        <div className='mg-b-78 mg-t-xxl-100 mg-t-xs-50 pd-t-md-30 pd-t-xs-0 d-flex justify-content-center align-items-center'>
          <img src={Images.logo} alt="logo" className="eyworks-logo" />
        </div>
        <p className='fs-4 text-black fw-bold'>Sign In</p>
        <p className='fs-14 text-black mg-b-28'>Please enter your password to sign in.</p>
        <div className='mg-b-25'>
          <CustomTextInput
            type={'email'}
            name={'email'}
            value={loginData.email}
            onChange={handleInputChange}
            label={'Email'}
            icon={<CheckmarkCircle
              color={'#24B282'}
              height="20px"
              width="20px"
            />}
            disabled={true}
            handleKeyDown={handleKeyDown}
          />
          {errors && errors[FIELD_MAP.EMAIL] && errors[FIELD_MAP.EMAIL].isErrored ? <span className="form-error">{errors[FIELD_MAP.EMAIL].errorMsg}</span>: null}
        </div>
        <div className='mg-b-25'>
          <CustomTextInput
            type={showPassword ? 'text' : 'password'}
            name={'password'}
            value={loginData.password}
            onChange={handleInputChange}
            label={'Password'}
            icon={showPassword ? <Eye
              color={'rgba(0, 0, 0, 0.20)'}
              height="20px"
              width="20px"
            /> : <EyeOff
              color={'rgba(0, 0, 0, 0.20)'}
              height="20px"
              width="20px"
            />}
            isIconClick={() => setShowPassword(!showPassword)}
            inputRef={inputRef}
            handleKeyDown={handleKeyDown}
          />
          {errors && errors[FIELD_MAP.PASSWORD] && errors[FIELD_MAP.PASSWORD].isErrored ? <span className="form-error">{errors[FIELD_MAP.PASSWORD].errorMsg}</span>: null}
          <div className='d-flex justify-content-end'>
            <CustomButton text={'Forgot Password?'} isLoading={resending} onClick={handleAccountVerification} color={'link'} customBtnStyle={'custom-btn-link'} />
          </div>
        </div>
        <CustomButton text={'Sign in'} onClick={handleLogin} isLoading={isLoggingIn} color={'primary'} buttonRef={buttonRef}/>
      </div>
      <div className='mg-b-10'>
        <DownloadAppComponent />
        <Footer />
      </div>
    </div>
  )
}

export default LoginScreen;